import { useId, useRef, useState } from "react";

const RATINGS = [1, 2, 3, 4, 5];
const STAR_PATH = "m12 3 2.78 5.63L21 9.54l-4.5 4.39 1.06 6.2L12 17.2l-5.56 2.93 1.06-6.2L3 9.54l6.22-.91L12 3Z";

export default function FiveStarRating({ movieTitle, value, onChange }) {
  const [preview, setPreview] = useState(null);
  const buttons = useRef([]);
  const descriptionId = useId();
  const displayed = preview ?? value ?? 0;
  const tabStop = RATINGS.includes(value) ? value : 1;

  const select = (rating) => {
    setPreview(null);
    if (rating !== value) onChange(rating);
  };

  const onKeyDown = (event, rating) => {
    let next;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = rating % 5 + 1;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (rating + 3) % 5 + 1;
    else if (event.key === "Home") next = 1;
    else if (event.key === "End") next = 5;
    else return; // Native buttons handle Space and Enter.
    event.preventDefault();
    buttons.current[next - 1].focus();
    select(next);
  };

  return (
    <div
      role="radiogroup"
      aria-label={`Rating for ${movieTitle}`}
      aria-describedby={descriptionId}
      className="flex shrink-0 flex-wrap items-center gap-x-1"
      onMouseLeave={() => setPreview(null)}
    >
      <div className="flex">
        {RATINGS.map((rating, index) => {
          // Preserve fractional existing values visually; choosing a star
          // still uses the existing whole-number 1–5 input scale.
          const fill = Math.min(1, Math.max(0, displayed - index)) * 100;
          return (
            <button
              key={rating}
              ref={(element) => { buttons.current[index] = element; }}
              type="button"
              role="radio"
              aria-label={`${rating} ${rating === 1 ? "star" : "stars"}`}
              aria-checked={value === rating}
              tabIndex={tabStop === rating ? 0 : -1}
              onClick={() => select(rating)}
              onMouseEnter={() => setPreview(rating)}
              onKeyDown={(event) => onKeyDown(event, rating)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[#FFF2B3] transition-colors hover:bg-[#FFF2B3]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFF2B3]"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6">
                <path d={STAR_PATH} fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d={STAR_PATH} fill="currentColor" style={{ clipPath: `inset(0 ${100 - fill}% 0 0)` }} />
              </svg>
            </button>
          );
        })}
      </div>
      <p id={descriptionId} className="min-w-10 text-right text-[11px] tabular-nums text-[#FFF2B3]">
        {preview !== null ? `Preview: ${preview}/5` : value == null ? "Unrated" : `${value}/5`}
        <span className="sr-only">. Use arrow keys to change the rating, or press Space or Enter to select a star.</span>
      </p>
    </div>
  );
}
